import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { studentService } from '@services/student/studentService';
import { FileText, Award, Calendar, CheckCircle, Clock, PlayCircle, Eye, User, XCircle, Code2, ArrowRight } from 'lucide-react';
import { useRipple } from '@/hooks/useRipple';

const formatClassTime = (timeStr: string | null | undefined) => {
    if (!timeStr) return '';
    try {
        const [hoursStr, minutesStr] = timeStr.split(':');
        const hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);
        if (isNaN(hours) || isNaN(minutes)) return timeStr;
        
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        return `${formattedHours}:${formattedMinutes} ${ampm}`;
    } catch {
        return timeStr;
    }
};

const isSameDay = (d1: string | Date | undefined, d2: string | Date | undefined): boolean => {
    if (!d1 || !d2) return false;
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return false;
    
    const iso1 = typeof d1 === 'string' && d1.includes('T') ? d1.split('T')[0] : date1.toISOString().split('T')[0];
    const iso2 = typeof d2 === 'string' && d2.includes('T') ? d2.split('T')[0] : date2.toISOString().split('T')[0];
    if (iso1 === iso2) return true;

    return (
        (date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate()) ||
        (date1.getUTCFullYear() === date2.getUTCFullYear() &&
         date1.getUTCMonth() === date2.getUTCMonth() &&
         date1.getUTCDate() === date2.getUTCDate())
    );
};

const getAttendanceRecord = (dayDate: string | Date, attendanceList?: any[]) => {
    if (!attendanceList || !attendanceList.length) return null;
    return attendanceList.find((att: any) => isSameDay(att.date, dayDate));
};

const renderAttendanceBadge = (status: string | null | undefined, isPastOrToday: boolean) => {
    const normalizedStatus = status?.toUpperCase();
    if (normalizedStatus === 'PRESENT') {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[11px] font-bold shadow-xs">
                <CheckCircle size={12} className="text-emerald-600 shrink-0" />
                Present
            </span>
        );
    }
    if (normalizedStatus === 'ABSENT') {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-[11px] font-bold shadow-xs">
                <XCircle size={12} className="text-rose-600 shrink-0" />
                Absent
            </span>
        );
    }
    if (normalizedStatus === 'LATE') {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[11px] font-bold shadow-xs">
                <Clock size={12} className="text-amber-600 shrink-0" />
                Late
            </span>
        );
    }
    if (isPastOrToday) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 text-slate-400 border border-slate-200 rounded-full text-[10px] font-semibold">
                <Clock size={11} className="shrink-0" />
                Not Marked
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-bg text-primary border border-primary/20 rounded-full text-[10px] font-semibold">
            Upcoming
        </span>
    );
};

const StudentDashboard: React.FC = () => {
    const { t } = useTranslation('students');
    const { user } = useAuth();
    const navigate = useNavigate();
    const ripple = useRipple();
    const [dashboardData, setDashboardData] = useState<any>(null);
    const [scheduleData, setScheduleData] = useState<any[]>([]);
    const [documentsData, setDocumentsData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [sessionFilter, setSessionFilter] = useState<'upcoming' | 'all' | 'past'>('upcoming');

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                const [dashboardRes, scheduleRes, docsRes] = await Promise.all([
                    studentService.getDashboard(),
                    studentService.getSchedule(),
                    studentService.getDocuments(),
                ]);
                
                setDashboardData(dashboardRes.data.data);
                const schedule = scheduleRes.data.data || [];
                setScheduleData(schedule);
                setDocumentsData(docsRes.data.data);
            } catch (error) {
                console.error("Failed to fetch student data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, []);

    const allSessions = useMemo(() => {
        const list: any[] = [];
        scheduleData.forEach((week: any) => {
            if (week.days) {
                week.days.forEach((day: any) => {
                    if (day.is_active !== false) {
                        list.push({
                            ...day,
                            week_number: week.week_number,
                            week_title: week.title,
                        });
                    }
                });
            }
        });

        list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        return list;
    }, [scheduleData]);

    const displayedSessions = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (sessionFilter === 'upcoming') {
            const upcoming = allSessions.filter((day: any) => {
                const dayDate = new Date(day.date);
                dayDate.setHours(0, 0, 0, 0);
                return dayDate >= today;
            });
            return upcoming;
        }
        if (sessionFilter === 'past') {
            const past = allSessions.filter((day: any) => {
                const dayDate = new Date(day.date);
                dayDate.setHours(0, 0, 0, 0);
                return dayDate < today;
            });
            return [...past].reverse();
        }
        return allSessions;
    }, [allSessions, sessionFilter]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="text-center py-10 bg-white rounded-2xl border border-border shadow-soft">
                <h3 className="text-lg font-medium text-navy">{t('portal.dashboard.noEnrollmentTitle')}</h3>
                <p className="text-slate-body mt-2">{t('portal.dashboard.noEnrollmentMsg')}</p>
            </div>
        );
    }

    const { enrollment, cohort, progress } = dashboardData;

    const getFileUrl = (path: string) => {
        if (!path) return '#';
        if (path.startsWith('http')) return path;
        const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:5050';
        return `${baseUrl}/${path.startsWith('/') ? path.slice(1) : path}`;
    };

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    const upcomingCount = allSessions.filter((d: any) => {
        const dt = new Date(d.date);
        dt.setHours(0, 0, 0, 0);
        return dt >= todayDate;
    }).length;

    const pastCount = allSessions.filter((d: any) => {
        const dt = new Date(d.date);
        dt.setHours(0, 0, 0, 0);
        return dt < todayDate;
    }).length;

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-white rounded-2xl p-6 shadow-soft border border-border flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-bg rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/3"></div>
                <div className="relative z-10">
                    <h1 className="text-2xl font-bold text-navy mb-1">{t('portal.dashboard.welcome', { name: user?.name })}</h1>
                    <p className="text-slate-body font-medium">{t('portal.dashboard.cohortLabel', { title: cohort.title })}</p>
                    <div className="mt-4 inline-flex items-center gap-2 bg-blue-bg text-primary px-3 py-1.5 rounded-full text-sm font-semibold">
                        <CheckCircle size={16} />
                        {t('portal.dashboard.enrollmentLabel', { number: enrollment.number })}
                    </div>
                </div>

                <div className="relative z-10 w-full md:w-64 bg-blue-bg/50 rounded-xl p-4 border border-border">
                    <div className="flex justify-between items-end mb-2">
                        <span className="text-sm font-semibold text-slate-body">{t('portal.dashboard.progressTitle')}</span>
                        <span className="text-xl font-bold text-primary">{progress.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                        <div className="bg-primary h-2.5 rounded-full transition-all duration-1000" style={{ width: `${progress.percentage}%` }}></div>
                    </div>
                    <p className="text-xs text-slate-body text-right">{t('portal.dashboard.progressDay', { passed: progress.days_passed, total: progress.total_days })}</p>
                </div>
            </div>

            {/* Quick Coding Challenges CTA Banner */}
            <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-2xl p-5 shadow-soft border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                    <div className="p-3 bg-white/10 rounded-xl text-emerald-400 shrink-0">
                        <Code2 size={24} />
                    </div>
                    <div>
                        <h3 className="font-bold text-sm text-white">Cohort Coding Tasks & Challenges</h3>
                        <p className="text-xs text-slate-300">Practice Python algorithm challenges assigned to your cohort with automated test evaluation.</p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/student/weeks/1')}
                    className="px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all cursor-pointer shrink-0 shadow-sm"
                >
                    Solve Challenges
                    <ArrowRight size={14} />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content Area - Schedule */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                            <Calendar className="text-primary" />
                            Schedule & Sessions
                        </h2>

                        <div className="flex items-center bg-slate-100/90 p-1 rounded-xl border border-border w-fit text-xs font-semibold">
                            <button
                                type="button"
                                onClick={() => setSessionFilter('upcoming')}
                                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                                    sessionFilter === 'upcoming'
                                        ? 'bg-white text-primary shadow-xs font-bold'
                                        : 'text-slate-body hover:text-navy'
                                }`}
                            >
                                Upcoming ({upcomingCount})
                            </button>
                            <button
                                type="button"
                                onClick={() => setSessionFilter('all')}
                                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                                    sessionFilter === 'all'
                                        ? 'bg-white text-primary shadow-xs font-bold'
                                        : 'text-slate-body hover:text-navy'
                                }`}
                            >
                                All ({allSessions.length})
                            </button>
                            <button
                                type="button"
                                onClick={() => setSessionFilter('past')}
                                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                                    sessionFilter === 'past'
                                        ? 'bg-white text-primary shadow-xs font-bold'
                                        : 'text-slate-body hover:text-navy'
                                }`}
                            >
                                Past ({pastCount})
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {displayedSessions.length === 0 ? (
                            <div className="text-center py-10 bg-white rounded-2xl border border-border shadow-soft">
                                <h3 className="text-sm font-semibold text-navy">
                                    {sessionFilter === 'upcoming'
                                        ? 'No Upcoming Sessions'
                                        : sessionFilter === 'past'
                                        ? 'No Past Sessions'
                                        : 'No Scheduled Sessions'}
                                </h3>
                                <p className="text-xs text-slate-body mt-2">
                                    {sessionFilter === 'upcoming'
                                        ? 'All scheduled sessions have already taken place. Switch to "All" or "Past" to view previous sessions.'
                                        : 'There are no sessions to display for this filter.'}
                                </p>
                            </div>
                        ) : (
                            displayedSessions.map((day: any) => {
                                const studentLinkItem = day.is_mentor_session
                                    ? day.student_links?.find((sl: any) => sl.enrollment_id === enrollment?.id)
                                    : null;
                                const resolvedLink = day.is_mentor_session
                                    ? (studentLinkItem?.link || '')
                                    : day.link;
                                const hasLink = !!resolvedLink;
                                const resolvedTime = day.is_mentor_session
                                    ? (studentLinkItem?.meeting_time || '')
                                    : (day.class_time || '');
                                const resolvedDescription = day.is_mentor_session
                                    ? (studentLinkItem?.description || day.description || '')
                                    : (day.description || '');

                                const attRecord = getAttendanceRecord(day.date, enrollment?.attendance);
                                const attendanceStatus = day.attendance_status || attRecord?.status || null;

                                const dayDate = new Date(day.date);
                                const sessionDayStart = new Date(dayDate);
                                sessionDayStart.setHours(0, 0, 0, 0);
                                const isPastOrToday = sessionDayStart <= todayDate;

                                return (
                                <div
                                    key={`${day.week_number}-${day.day_number}-${day.id || day.date}`}
                                    className={`bg-white rounded-2xl shadow-soft border border-border overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card p-5 ${
                                        day.is_unlocked ? '' : 'opacity-70 bg-gray-50/50'
                                    }`}
                                >
                                    <div className="flex flex-col gap-3">
                                        {/* Date and Meta Row */}
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {day.is_mentor_session && (
                                                        <span className="text-[9px] font-bold text-indigo-700 px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-full uppercase tracking-wider">
                                                            Mentor Session
                                                        </span>
                                                    )}
                                                    <span className="text-[9px] font-bold text-slate-500 px-2 py-0.5 bg-slate-100 rounded-full">
                                                        Week {day.week_number} • Day {day.day_number}
                                                    </span>
                                                    {renderAttendanceBadge(attendanceStatus, isPastOrToday)}
                                                </div>

                                                <h3 className={`text-base font-bold leading-snug ${day.is_unlocked ? 'text-navy' : 'text-slate-body'}`}>
                                                    {day.title}
                                                </h3>
                                            </div>
                                            
                                            <div className="flex flex-col items-end shrink-0 text-right">
                                                <span className="text-xs font-black text-navy">
                                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                </span>
                                                {resolvedTime && (
                                                    <span className="text-[10px] font-bold text-primary mt-1 px-2 py-0.5 bg-blue-bg/60 rounded-full border border-primary/10">
                                                        {formatClassTime(resolvedTime)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {day.is_unlocked && resolvedDescription && (
                                            <p className="text-xs text-slate-body leading-relaxed whitespace-pre-wrap">
                                                {resolvedDescription}
                                            </p>
                                        )}

                                        {/* Actions block */}
                                        {day.is_unlocked && (
                                            <div className="flex flex-wrap items-center gap-3 pt-1">
                                                {day.is_mentor_session ? (
                                                    hasLink ? (
                                                        day.is_link_active ? (
                                                            <a
                                                                href={resolvedLink}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onPointerDown={ripple.onPointerDown}
                                                                className="ripple-trigger inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs font-bold transition-all cursor-pointer shadow-sm"
                                                            >
                                                                <PlayCircle size={14} className="mt-[-1px]" />
                                                                Join Mentor Session
                                                            </a>
                                                        ) : (
                                                            <div
                                                                title="Link activates 15 minutes before the session starts"
                                                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-50/50 text-indigo-500 rounded-full text-xs font-semibold border border-indigo-100 cursor-not-allowed select-none"
                                                            >
                                                                <Clock size={14} className="mt-[-1px]" />
                                                                Mentor Session (Active 15m before session{resolvedTime ? ` at ${formatClassTime(resolvedTime)}` : ''})
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div
                                                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-50 text-gray-400 rounded-full text-xs font-semibold border border-gray-200 cursor-not-allowed select-none"
                                                        >
                                                            <Clock size={14} className="mt-[-1px]" />
                                                            Mentor session link not set yet
                                                        </div>
                                                    )
                                                ) : day.link ? (
                                                    day.is_link_active ? (
                                                        <a
                                                            href={day.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onPointerDown={ripple.onPointerDown}
                                                            className="ripple-trigger inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/95 text-white rounded-full text-xs font-bold transition-all cursor-pointer shadow-sm"
                                                        >
                                                            <PlayCircle size={14} className="mt-[-1px]" />
                                                            Join Live Class
                                                        </a>
                                                    ) : (
                                                        <div
                                                            title="Link activates 15 minutes before the class starts"
                                                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-400 rounded-full text-xs font-semibold border border-gray-200 cursor-not-allowed select-none"
                                                        >
                                                            <Clock size={14} className="mt-[-1px]" />
                                                            Join Live Class (Active 15m before class{day.class_time ? ` at ${formatClassTime(day.class_time)}` : ''})
                                                        </div>
                                                    )
                                                ) : null}
                                                {(day.recorded_video || day.recorded_video_path || (day.recordings && day.recordings.length > 0)) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate('/student/recordings')}
                                                        onPointerDown={ripple.onPointerDown}
                                                        className="ripple-trigger inline-flex items-center gap-1.5 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-full text-xs font-bold border border-purple-200 transition-all cursor-pointer"
                                                    >
                                                        <PlayCircle size={14} className="mt-[-1px]" />
                                                        Watch Recording
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {/* Locked state */}
                                        {!day.is_unlocked && (
                                            <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1 font-semibold">
                                                <Clock size={12} />
                                                Unlocks on {new Date(day.date).toLocaleDateString()}
                                            </p>
                                        )}

                                        {/* Class Resources */}
                                        {day.is_unlocked && day.attachments && day.attachments.length > 0 && (
                                            <div className="mt-1 bg-slate-50 border border-border rounded-xl p-3 space-y-2">
                                                <span className="text-[10px] uppercase font-extrabold tracking-wider text-slate-400 block">Class Resources</span>
                                                <div className="flex flex-col gap-2">
                                                    {day.attachments.map((attachment: string, index: number) => {
                                                        let parsedAttachment = { filePath: attachment, fileName: attachment, fileSize: 'N/A' };
                                                        try {
                                                            if (attachment.startsWith('{') && attachment.endsWith('}')) {
                                                                parsedAttachment = JSON.parse(attachment);
                                                            } else {
                                                                parsedAttachment.fileName = attachment.split('/').pop()?.replace(/^\d+-/, '') || attachment;
                                                            }
                                                        } catch (e) {}
                                                        return (
                                                            <a
                                                                key={index}
                                                                href={getFileUrl(parsedAttachment.filePath)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                                                            >
                                                                <FileText size={12} className="text-slate-400" />
                                                                {parsedAttachment.fileName} <span className="text-gray-400 text-[10px] font-normal">({parsedAttachment.fileSize})</span>
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )})
                        )}
                    </div>
                </div>

                {/* Sidebar Area - Attendance & Documents */}
                <div className="space-y-6">
                    {/* Attendance Card */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                            <CheckCircle className="text-primary" />
                            Attendance Overview
                        </h2>

                        <div className="bg-white rounded-xl shadow-soft border border-border p-5 space-y-4">
                            {/* Attendance Stats Header */}
                            <div className="flex items-center justify-between gap-4">
                                <div className="space-y-1">
                                    <span className="text-3xl font-extrabold text-navy">
                                        {enrollment.attendance && enrollment.attendance.length > 0
                                            ? `${Math.round(
                                                (enrollment.attendance.filter((a: any) => a.status === 'PRESENT' || a.status === 'LATE').length /
                                                    enrollment.attendance.length) *
                                                100
                                            )}%`
                                            : '100%'}
                                    </span>
                                </div>
                                <div className="flex gap-2 text-center">
                                    <div className="bg-green-50 border border-green-100 rounded-lg px-2.5 py-1 text-green-700">
                                        <div className="text-[9px] uppercase font-black tracking-wider">Present</div>
                                        <div className="text-sm font-black mt-0.5">
                                            {enrollment.attendance?.filter((a: any) => a.status === 'PRESENT').length || 0}
                                        </div>
                                    </div>
                                    <div className="bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1 text-amber-700">
                                        <div className="text-[9px] uppercase font-black tracking-wider">Late</div>
                                        <div className="text-sm font-black mt-0.5">
                                            {enrollment.attendance?.filter((a: any) => a.status === 'LATE').length || 0}
                                        </div>
                                    </div>
                                    <div className="bg-rose-50 border border-rose-100 rounded-lg px-2.5 py-1 text-rose-700">
                                        <div className="text-[9px] uppercase font-black tracking-wider">Absent</div>
                                        <div className="text-sm font-black mt-0.5">
                                            {enrollment.attendance?.filter((a: any) => a.status === 'ABSENT').length || 0}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Timeline of Marked Days */}
                            <div className="space-y-2 pt-3 border-t border-border">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] uppercase font-extrabold tracking-wider text-slate-400 block">Marked Sessions</span>
                                    <span className="text-[10px] font-bold text-primary">{enrollment.attendance?.length || 0} Marked</span>
                                </div>
                                {!enrollment.attendance || enrollment.attendance.length === 0 ? (
                                    <div className="text-center py-4 bg-slate-50 border border-dashed border-gray-200 rounded-lg text-[11px] text-gray-400 font-semibold">
                                        No attendance records marked yet.
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto pr-1">
                                        {[...enrollment.attendance]
                                            .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
                                            .map((att: any) => {
                                                const attDate = new Date(att.date);
                                                const matchedSession = allSessions.find((s: any) => isSameDay(s.date, att.date));

                                                let badgeClass = 'bg-green-100 border-green-200 text-green-700';
                                                let statusText = 'Present';
                                                if (att.status === 'ABSENT') {
                                                    badgeClass = 'bg-rose-100 border-rose-200 text-rose-700';
                                                    statusText = 'Absent';
                                                } else if (att.status === 'LATE') {
                                                    badgeClass = 'bg-amber-100 border-amber-200 text-amber-700';
                                                    statusText = 'Late';
                                                }

                                                return (
                                                    <div key={att.id || att.date} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-100 rounded-xl">
                                                        <div className="flex flex-col text-left overflow-hidden mr-2">
                                                            <span className="text-xs font-bold text-navy truncate">
                                                                {matchedSession?.title || attDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </span>
                                                            <span className="text-[9px] text-gray-400 font-semibold mt-0.5">
                                                                {attDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                            </span>
                                                        </div>
                                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border shrink-0 ${badgeClass}`}>
                                                            {statusText}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                        <FileText className="text-primary" />
                        {t('portal.dashboard.docsTitle')}
                    </h2>

                    <div className="bg-white rounded-xl shadow-soft border border-border p-5 space-y-4">
                        {/* LOA */}
                        <div className="flex items-center justify-between p-3 border border-border rounded-xl hover:border-primary/30 transition-colors bg-blue-bg/30">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 text-green-700 rounded-full">
                                    <FileText size={20} />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-navy text-sm">{t('portal.dashboard.lor')}</h4>
                                    <p className="text-xs text-slate-body">
                                        {documentsData?.lor_path ? t('portal.dashboard.available') : t('portal.dashboard.pendingGen')}
                                    </p>
                                </div>
                            </div>
                            {documentsData?.lor_path && (
                                <a
                                    href={getFileUrl(documentsData.lor_path)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onPointerDown={ripple.onPointerDown}
                                    className="ripple-trigger flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary transition-colors bg-white px-3 py-1.5 rounded-full border border-border hover:border-primary/40"
                                >
                                    <Eye size={16} />
                                    {t('portal.dashboard.view', { defaultValue: 'View' })}
                                </a>
                            )}
                        </div>

                        {/* Certificate */}
                        <div className="flex items-center justify-between p-3 border border-border rounded-xl hover:border-primary/30 transition-colors bg-blue-bg/30">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-yellow-100 text-yellow-600 rounded-full">
                                    <Award size={20} />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-navy text-sm">{t('portal.dashboard.cert')}</h4>
                                    <p className="text-xs text-slate-body">
                                        {documentsData?.certificate ? t('portal.dashboard.awarded') : t('portal.dashboard.locked')}
                                    </p>
                                </div>
                            </div>
                            {documentsData?.certificate && (
                                <a
                                    href={getFileUrl(documentsData.certificate.certificate_url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onPointerDown={ripple.onPointerDown}
                                    className="ripple-trigger flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary transition-colors bg-white px-3 py-1.5 rounded-full border border-border hover:border-primary/40"
                                >
                                    <Eye size={16} />
                                    {t('portal.dashboard.view', { defaultValue: 'View' })}
                                </a>
                            )}
                        </div>

                        {/* Student Handbook */}
                        {dashboardData.cohort?.handbook_path && (
                            <div className="flex items-center justify-between p-3 border border-border rounded-xl hover:border-primary/30 transition-colors bg-blue-bg/30">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-100 text-purple-700 rounded-full">
                                        <FileText size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-navy text-sm">Student Handbook</h4>
                                        <p className="text-xs text-slate-body">
                                            Available
                                        </p>
                                    </div>
                                </div>
                                <a
                                    href={getFileUrl(dashboardData.cohort.handbook_path)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onPointerDown={ripple.onPointerDown}
                                    className="ripple-trigger flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary transition-colors bg-white px-3 py-1.5 rounded-full border border-border hover:border-primary/40 cursor-pointer"
                                >
                                    <Eye size={16} />
                                    {t('portal.dashboard.view', { defaultValue: 'View' })}
                                </a>
                            </div>
                        )}
                    </div>

                    {/* Assigned Mentor Card */}
                    <h2 className="text-xl font-bold text-navy flex items-center gap-2 pt-2">
                        <User className="text-primary" />
                        Mentor Details
                    </h2>
                    <div className="bg-white rounded-xl shadow-soft border border-border p-5">
                        {dashboardData.mentor ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-bg flex items-center justify-center font-bold text-primary text-sm border border-primary/15 shrink-0">
                                        {dashboardData.mentor.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || 'M'}
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-navy text-sm">{dashboardData.mentor.name}</h4>
                                        <p className="text-xs text-slate-body">{dashboardData.mentor.speciality}</p>
                                    </div>
                                </div>
                                <div className="pt-2.5 border-t border-border flex flex-col gap-1 text-xs">
                                    <span className="text-slate-body font-medium">Email Address</span>
                                    <a href={`mailto:${dashboardData.mentor.email}`} className="font-semibold text-primary hover:underline break-all">
                                        {dashboardData.mentor.email}
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4 text-xs text-slate-body">
                                Your 1-on-1 mentor will be assigned shortly.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentDashboard;
